import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import { MenuEditorDragData } from 'modules/menu/components/MenuEditorDnDContext';
import { getMenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext/utils';
import { solidify } from 'modules/menu/solidify';

import CategoryLinkItem from './CategoryLinkItem';

type SubcategoryNavItems = {
  menuEditorRootUrl: string;
  language: string;
  hasCategoryImages: boolean;
  subcategories: Category[];
  categoryId: string;
};

type DnDSubcategoryLinkItemProps = {
  children: ReactNode;
  categoryId: string;
  menuEditorRootUrl: string;
  language: string;
  hasCategoryImages: boolean;
  subcategory: Category;
};

const ItemDragOverlay = styled(CategoryLinkItem)`
  background-color: ${cssThemedValue({
    light: solidify(colors.pepper8, 'light'),
    dark: solidify(colors.salt8, 'dark'),
  })} !important;
`;

const DnDSubcategoryLinkItemRoot = React.memo(styled.div<{ isDragging?: boolean }>`
  margin-inline-end: -8px;
  margin-inline-start: -8px;
  padding-block-start: 4px;
  padding-block-end: 4px;
  opacity: ${({ isDragging }) => (isDragging ? 0.6 : 1)};
`);

const DnDSubcategoryLinkItem = ({
  children,
  subcategory,
  hasCategoryImages,
  menuEditorRootUrl,
  categoryId,
}: DnDSubcategoryLinkItemProps) => {
  const data: MenuEditorDragData = useMemo(
    () => ({
      type: 'DND_NAVIGATION_SUBCATEGORY',
      subcategory,
      parentCategoryId: categoryId,
      dragOverlay: (
        <DnDSubcategoryLinkItemRoot>
          <ItemDragOverlay
            numberOfItems={subcategory.item_bindings.length}
            imageUrl={hasCategoryImages && subcategory.image ? subcategory.image : null}
            imageBlurhash={subcategory.image_blurhash ?? null}
          >
            {children}
          </ItemDragOverlay>
        </DnDSubcategoryLinkItemRoot>
      ),
      shrinkOverlayTypes: [
        'DND_EDITOR_SINGLE_ITEM',
        'DND_EDITOR_MULTIPLE_ITEMS',
        'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
        'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
      ],
    }),
    [categoryId, children, hasCategoryImages, subcategory],
  );
  const sortable = useSortable({
    id: subcategory.id,
    data,
  });
  const dndStyle = useMemo(
    () => ({
      transform: CSS.Transform.toString(sortable.transform),
      transition: sortable.transition,
    }),
    [sortable.transform, sortable.transition],
  );
  const activeEvent = sortable.active && getMenuEditorDragEventData(sortable.active);
  const isDroppable = useMemo(
    () => !!activeEvent && data.shrinkOverlayTypes.includes(activeEvent.type) && sortable.isOver,

    [activeEvent, data.shrinkOverlayTypes, sortable.isOver],
  );
  return (
    <DnDSubcategoryLinkItemRoot
      style={dndStyle}
      {...sortable.listeners}
      {...sortable.attributes}
      ref={sortable.setNodeRef}
      isDragging={sortable.isDragging}
    >
      <CategoryLinkItem
        isDroppable={isDroppable}
        numberOfItems={subcategory.item_bindings.length}
        imageUrl={hasCategoryImages && subcategory.image ? subcategory.image : null}
        imageBlurhash={subcategory.image_blurhash ?? null}
        href={`${menuEditorRootUrl}/${categoryId}/subcategories/${subcategory.id}`}
      >
        {children}
      </CategoryLinkItem>
    </DnDSubcategoryLinkItemRoot>
  );
};

const MemoizedDnDSubcategoryLinkItem = React.memo(DnDSubcategoryLinkItem);

const SubcategoryNavItems = ({
  subcategories,
  menuEditorRootUrl,
  hasCategoryImages,
  language,
  categoryId,
}: SubcategoryNavItems) => {
  return (
    /* Ignore MenuGroup styles */
    <span>
      {subcategories.map(subcategory => (
        <MemoizedDnDSubcategoryLinkItem
          key={subcategory.id}
          categoryId={categoryId}
          menuEditorRootUrl={menuEditorRootUrl}
          language={language}
          subcategory={subcategory}
          hasCategoryImages={hasCategoryImages}
        >
          {localize(subcategory.name, language)}
        </MemoizedDnDSubcategoryLinkItem>
      ))}
    </span>
  );
};

export default React.memo(SubcategoryNavItems);
